import * as React from "react";
import { Box, Typography, TextField, MenuItem } from "@mui/material";
import { Helmet } from "react-helmet";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { BG, CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, MONO_FONT_FAMILY, BAD_RED_TEXT, FA_BOTTOM_NAV_HEIGHT } from "./designTokens";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    backgroundColor: BG,
    "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
    "&.Mui-focused fieldset": { borderColor: AMBER },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiInputLabel-root.Mui-focused": { color: AMBER },
};

export function FATextField(props) {
  return <TextField fullWidth variant="outlined" sx={FIELD_SX} {...props} />;
}

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: CARD_BG,
      border: `1px solid ${BORDER}`,
      "& .MuiMenuItem-root": { color: "#fff" },
      "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
      "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "rgba(253,185,64,0.14)" },
    },
  },
};

// sx is merged rather than spread past FIELD_SX. Props land after sx on the element below,
// so a caller passing sx would otherwise replace the shared field styling wholesale instead
// of adding to it — silently losing the border, focus and label colours.
export function FASelect({ children, sx, ...props }) {
  return (
    <TextField fullWidth select variant="outlined" sx={{ ...FIELD_SX, ...sx }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }} {...props}>
      {children}
    </TextField>
  );
}

export { MenuItem };

// The stock screens' field, from the designs' .form-row / .form-input / .form-textarea.
// One shape across collect and returns: a plain label above a filled, rounded box that warms
// to the accent on focus. MUI's floating-label TextField is a different control — the label
// sits inside the border and animates, which is why these screens read as another app.
const FIELD_BOX_SX = {
  width: "100%",
  boxSizing: "border-box",
  p: "11px 14px",
  backgroundColor: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#fff",
  borderRadius: "10px",
  outline: "none",
  "&:focus": { borderColor: "rgba(253,185,64,0.4)" },
  "&::placeholder": { color: "rgba(255,255,255,0.3)" },
};

export function FAFieldRow({ label, hint, htmlFor, children }) {
  return (
    <Box>
      {label ? (
        <Typography component="label" htmlFor={htmlFor} sx={{ display: "block", fontSize: "0.74rem", color: "rgba(255,255,255,0.55)", fontWeight: 500, mb: "6px" }}>
          {label}
        </Typography>
      ) : null}
      {children}
      {hint ? (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", mt: "4px" }}>{hint}</Typography>
      ) : null}
    </Box>
  );
}

export function FAFormInput({ mono, sx, ...props }) {
  return (
    <Box
      component="input"
      {...props}
      sx={{ ...FIELD_BOX_SX, fontSize: "0.9rem", fontFamily: mono ? MONO_FONT_FAMILY : FONT_FAMILY, ...sx }}
    />
  );
}

export function FAFormTextarea({ sx, ...props }) {
  return (
    <Box
      component="textarea"
      {...props}
      sx={{ ...FIELD_BOX_SX, minHeight: "80px", fontSize: "0.88rem", fontFamily: FONT_FAMILY, lineHeight: 1.45, resize: "vertical", ...sx }}
    />
  );
}

// The same box, applied to MUI's Select so the closed control matches the fields beside it.
export const FA_FORM_SELECT_SX = {
  "& .MuiOutlinedInput-notchedOutline": { border: 0 },
  "& .MuiSelect-select.MuiSelect-select": {
    p: "11px 14px",
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    color: "#fff",
    fontFamily: FONT_FAMILY,
    fontSize: "0.9rem",
    minHeight: "unset",
  },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
};

export function FormPage({ eyebrow, title, subtitle, children, onSubmit, submitLabel, submitDisabled, summary, error, hideActions }) {
  const navigate = useNavigate();

  return (
    <>
      {/* font-family was missing here, so these three routes rendered in MUI's Roboto
          fallback while every other Field Admin page used Inter. */}
      <Helmet>
        <style>{`body { background: ${BG}; font-family: ${FONT_FAMILY}; }`}</style>
      </Helmet>

      {/* pt clears the shared top bar, which these routes now render like every other
          Field Admin page; pb clears the sticky action bar plus the bottom nav. */}
      <Box sx={{ px: { xs: "18px", sm: "28px" }, pt: "83px", pb: "170px", maxWidth: "860px", mx: "auto" }}>
        <Box sx={{ mb: "22px" }}>
          {eyebrow ? (
            <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: AMBER, textTransform: "uppercase", letterSpacing: "0.1em", mb: "6px" }}>
              {eyebrow}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: { xs: "1.35rem", sm: "1.55rem" }, fontWeight: 700, color: "#fff", letterSpacing: "-0.025em" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "4px", lineHeight: 1.45 }}>
            {subtitle}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {children}
        </Box>

        {error ? (
          <Typography sx={{ color: BAD_RED_TEXT, fontSize: "0.82rem", mt: "16px" }}>{error}</Typography>
        ) : null}
      </Box>

      {/* Sticky action bar with a summary and a Cancel, per the design — the submit was
          previously an inline div with no way out of the form but the back arrow.
          Hidden where the page is showing a record rather than collecting one: a submit
          button over a read-only list has nothing to submit. */}
      {hideActions ? null : <Box
        sx={{
          position: "fixed", left: 0, right: 0, bottom: FA_BOTTOM_NAV_HEIGHT,
          backgroundColor: "rgba(13,17,23,0.94)", backdropFilter: "blur(10px)",
          borderTop: "1px solid rgba(255,255,255,0.06)", pt: "12px", pb: "14px", zIndex: 79,
        }}
      >
        <Box sx={{ px: { xs: "18px", sm: "28px" }, maxWidth: "860px", mx: "auto", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Typography sx={{ flex: 1, minWidth: "140px", fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>
            {summary}
          </Typography>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <FAGhostButton onClick={() => navigate("/field-admin/stock/")} sx={{ py: "11px" }}>
              Cancel
            </FAGhostButton>
            <FAPrimaryButton onClick={onSubmit} disabled={submitDisabled} sx={{ px: "18px", py: "11px", fontSize: "0.88rem" }}>
              {submitLabel}
            </FAPrimaryButton>
          </Box>
        </Box>
      </Box>}
    </>
  );
}

// A device QR encodes a URL ending /{serial_number}/{imsi_number}/ — the same shape the
// Installer app's QRCodeScanPage parses. Scanning one and treating the whole string as a
// serial would store the URL as the device serial, so the pair is split out here.
// Tolerant by design: a manually typed serial has no IMSI, and that is a valid entry.
export function parseDeviceQr(text) {
  const parts = String(text || "").trim().split("/");
  if (parts.length >= 7) {
    return { serial: parts[5], imsi: parts[6] || null };
  }
  return { serial: String(text || "").trim(), imsi: null };
}

// The design's camera viewport: a framed live preview with a dashed reticle and a sweeping
// laser. Amber on collect, red on returns — each page's own accent (the .scan-frame /
// .scan-corner-* / .scan-laser rules in stock-collect.html and stock-returns.html
// respectively). react-qr-reader is already a dependency and already used by
// three Installer pages, so this is the same scanner in Field Admin dress.
export function ScanViewport({ tone = "amber", hint, onScan, scannedLabel }) {
  const accent = tone === "red" ? "#F87171" : AMBER;
  const frameBorder = tone === "red" ? "rgba(239,68,68,0.4)" : "rgba(253,185,64,0.4)";

  // react-qr-reader@3.0.0-beta-1 registers its result callback inside a useEffect with an
  // empty dependency array, so the function it keeps is the one from the first render and
  // never updates. Any handler that reads state therefore reads it as it was at mount:
  // the collect screen's already-scanned check tested an empty list every time, so holding
  // one QR in frame appended the same device once per decode tick — and submitted a POST
  // per row against an endpoint that does not dedupe by serial. A ref keeps the current
  // handler reachable behind a callback identity the library is welcome to freeze.
  const onScanRef = React.useRef(onScan);
  React.useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  const handleResult = React.useCallback((result) => {
    if (result) onScanRef.current(result.text);
  }, []);

  return (
    <Box>
      <Box
        sx={{
          position: "relative", width: "100%", aspectRatio: tone === "red" ? "16 / 9" : "16 / 11",
          // The design's viewport sits inside a phone frame; on a desktop-width
          // container the raw aspect ratio would be ~570px tall, so cap it.
          maxHeight: "340px", mx: "auto",
          backgroundColor: "#000", borderRadius: "14px", overflow: "hidden",
          "& video": { width: "100%", height: "100%", objectFit: "cover" },
          "& section > div": { paddingTop: "0 !important", boxShadow: "none !important", border: "none !important" },
        }}
      >
        <QrReader
          constraints={{ facingMode: "environment" }}
          scanDelay={400}
          onResult={handleResult}
          containerStyle={{ width: "100%", height: "100%" }}
          videoContainerStyle={{ width: "100%", height: "100%", paddingTop: 0 }}
          videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
        />

        {/* Reticle + laser, drawn over the preview. */}
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <Box sx={{ position: "relative", width: tone === "red" ? "38%" : "44%", aspectRatio: "1", border: `2px dashed ${frameBorder}`, borderRadius: "10px" }}>
            <Box
              sx={{
                position: "absolute", left: 0, right: 0, height: "2px", backgroundColor: accent,
                boxShadow: `0 0 10px ${accent}`, animation: "fa-laser 2.5s ease-in-out infinite",
                "@keyframes fa-laser": { "0%, 100%": { top: "4%" }, "50%": { top: "94%" } },
              }}
            />
          </Box>
        </Box>

        {scannedLabel ? (
          <Box sx={{ position: "absolute", top: "14px", left: "14px", px: "10px", py: "5px", borderRadius: "999px", backgroundColor: "rgba(13,17,23,0.8)", color: accent, fontSize: "0.72rem", fontWeight: 700 }}>
            {scannedLabel}
          </Box>
        ) : null}
      </Box>
      {hint ? (
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.45)", textAlign: "center", mt: "8px" }}>{hint}</Typography>
      ) : null}
    </Box>
  );
}
